// ===========================================================================
// PatientSearch — componente transversal de búsqueda y selección de paciente.
//
// Pensado para módulos donde se INSERTA información: primero identificas al
// paciente por nombre y/o fecha de nacimiento, lo eliges de un dialog con los
// similares, y el componente entrega su ficha completa al padre vía onSelect.
//
// Flujo:
//   1. Inputs: nombre del paciente + fecha de nacimiento.
//   2. "Buscar" (habilitado con ≥2 letras del nombre o una DOB) → abre el
//      dialog con los pacientes similares (searchPatients → Patient Map V2).
//   3. Seleccionas uno → onSelect(patient) con TODA la info, y queda mostrado
//      como paciente activo (con botón para cambiarlo/limpiarlo).
//
// Uso:
//   <PatientSearch onSelect={(p) => cargarDatosDe(p.cipId)} />
//   <PatientSearch value={patient} onSelect={...} locationId={5} />
//
// Es self-contained (no se inyecta en ningún módulo todavía). Requiere el
// LocalizationProvider de @mui/x-date-pickers montado a nivel app (ya lo está).
// ===========================================================================
import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  Paper,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import PersonIcon from "@mui/icons-material/Person";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import PatientResultsDialog from "./PatientResultsDialog";
import { PatientDetailCard } from "../PatientDetailCard";
import { searchPatients } from "../../services/patientSearchService";
import { formatDate, safe } from "../../utils/format";

export default function PatientSearch({
  onSelect,
  value = null,
  locationId = null,
  pageSize = 25,
  title = "Find patient",
  helperText = "Fill in at least one field (name or date of birth), then press Search.",
  disabled = false,
  autoFocus = false,
  showDetail = true,
}) {
  const [name, setName] = useState("");
  const [dob, setDob] = useState(null); // dayjs | null
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  // Permite que el padre controle el paciente seleccionado (modo controlado);
  // si no, lo gestiona internamente.
  const [internalSelected, setInternalSelected] = useState(null);
  const selected = value !== undefined && value !== null ? value : internalSelected;

  const dobString = useMemo(
    () => (dob && dob.isValid?.() ? dob.format("YYYY-MM-DD") : null),
    [dob]
  );

  // Mínimo UN campo (no ambos): nombre no vacío O fecha de nacimiento.
  const canSearch = !disabled && (name.trim().length > 0 || !!dobString);

  const criteriaLabel = useMemo(() => {
    const parts = [];
    if (name.trim()) parts.push(`Name: "${name.trim()}"`);
    if (dobString) parts.push(`DOB: ${formatDate(dobString)}`);
    return parts.join(" · ");
  }, [name, dobString]);

  const runSearch = async () => {
    if (!canSearch) return;
    setDialogOpen(true);
    setLoading(true);
    setError(null);
    setResults([]);
    try {
      const { patients, hasMore: more } = await searchPatients({
        name: name.trim() || null,
        dateOfBirth: dobString,
        locationId,
        pageSize,
      });
      setResults(patients);
      setHasMore(more);
    } catch (err) {
      setError(
        err?.response?.status === 401
          ? "Your session expired. Please sign in again."
          : "Could not search patients. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && canSearch) {
      e.preventDefault();
      runSearch();
    }
  };

  const handlePick = (patient) => {
    setInternalSelected(patient);
    setDialogOpen(false);
    onSelect?.(patient);
  };

  const clearSelection = () => {
    setInternalSelected(null);
    onSelect?.(null);
  };

  // Limpia solo los criterios de búsqueda (nombre + DOB), para consultar otro
  // paciente sin tocar la selección actual.
  const hasFilters = name.trim().length > 0 || !!dobString;
  const clearFilters = () => {
    setName("");
    setDob(null);
  };

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {title}
        </Typography>
      )}

      {/* Campos de búsqueda */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
        <TextField
          size="small"
          label="Patient name"
          placeholder="Last, First"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <DatePicker
          label="Date of birth"
          value={dob}
          onChange={(d) => setDob(d)}
          format="MM/DD/YYYY"
          disabled={disabled}
          disableFuture
          slotProps={{
            textField: {
              size: "small",
              onKeyDown: handleKeyDown,
              sx: { width: { xs: "100%", sm: 190 } },
            },
            field: { clearable: true, onClear: () => setDob(null) },
          }}
        />
      </Stack>

      {/* Botones en una fila aparte (evita que se estrechen en pantallas chicas) */}
      <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={runSearch}
          disabled={!canSearch}
          sx={{ minWidth: 120 }}
        >
          Buscar
        </Button>

        <Tooltip title="Clear search filters (name and date of birth)">
          <span>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ClearAllIcon />}
              onClick={clearFilters}
              disabled={disabled || !hasFilters}
              sx={{ minWidth: 110 }}
            >
              Clear
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          {helperText}
        </Typography>
      )}

      {/* Paciente seleccionado */}
      {selected && (
        <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, borderRadius: 2, borderColor: "primary.light" }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <PersonIcon color="primary" sx={{ mt: 0.25 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {selected.memberFullName || "—"}
                </Typography>
                {selected.memberType && (
                  <Chip label={selected.memberType} size="small" sx={{ height: 18, fontSize: "0.62rem" }} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                DOB {formatDate(selected.dateOfBirth)} · {safe(selected.age)}y · {safe(selected.gender)} · CIP {safe(selected.cipId)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {safe(selected.medicalCenter)} · PCP {safe(selected.pcpName)}
              </Typography>
            </Box>
            <Tooltip title="Clear selection">
              <IconButton size="small" onClick={clearSelection} aria-label="Clear selected patient">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      {/* Tarjeta de detalle con tabs (demografía, care gaps, coding opportunities) */}
      {selected && showDetail && <PatientDetailCard patient={selected} />}

      <PatientResultsDialog
        open={dialogOpen}
        loading={loading}
        error={error}
        patients={results}
        hasMore={hasMore}
        criteria={criteriaLabel}
        onSelect={handlePick}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}
